import create from 'zustand';

/**
 * Chift export loading state per universe (transaction = banks, revenue, expense).
 * Flow: ChiftSyncForm triggers usePerformExport with syncChiftLoading → hook writes
 * activeExportId here; ChiftExportProvider (mounted on customer routes only, where
 * on-behalf header is sent) polls with the same React Query key and shows completion
 * toast, then clears this slice.
 */
export type ChiftExportUniverse = 'transaction' | 'revenue' | 'expense';

export interface ChiftExportToastContext {
  integrationName: string;
  type: string;
  /** Customer whose export completed; for "Show details" URL and for polling header x-on-behalf-of. */
  customerId?: string | null;
}

/** Map ExportedDocumentType / route type to store universe (transaction = banks). */
export const CHIFT_UNIVERSE_STORE_KEYS: Record<
  ChiftExportUniverse,
  'Banks' | 'Revenues' | 'Expenses'
> = {
  transaction: 'Banks',
  revenue: 'Revenues',
  expense: 'Expenses',
};

export interface ChiftExportUniverseState {
  isInProgress: boolean;
  activeExportId: string | null;
  toastContext: ChiftExportToastContext | null;
}

export interface ChiftExportLoadingStore {
  isBanksExportInProgress: boolean;
  setBanksExportInProgress: (value: boolean) => void;
  isRevenuesExportInProgress: boolean;
  setRevenuesExportInProgress: (value: boolean) => void;
  isExpensesExportInProgress: boolean;
  setExpensesExportInProgress: (value: boolean) => void;
  activeChiftExportIdBanks: string | null;
  setActiveChiftExportIdBanks: (id: string | null) => void;
  activeChiftExportIdRevenues: string | null;
  setActiveChiftExportIdRevenues: (id: string | null) => void;
  activeChiftExportIdExpenses: string | null;
  setActiveChiftExportIdExpenses: (id: string | null) => void;
  chiftExportToastContextBanks: ChiftExportToastContext | null;
  setChiftExportToastContextBanks: (
    ctx: ChiftExportToastContext | null,
  ) => void;
  chiftExportToastContextRevenues: ChiftExportToastContext | null;
  setChiftExportToastContextRevenues: (
    ctx: ChiftExportToastContext | null,
  ) => void;
  chiftExportToastContextExpenses: ChiftExportToastContext | null;
  setChiftExportToastContextExpenses: (
    ctx: ChiftExportToastContext | null,
  ) => void;
}

const store = create<ChiftExportLoadingStore>((set) => ({
  isBanksExportInProgress: false,
  setBanksExportInProgress: (value) => set({ isBanksExportInProgress: value }),
  isRevenuesExportInProgress: false,
  setRevenuesExportInProgress: (value) =>
    set({ isRevenuesExportInProgress: value }),
  isExpensesExportInProgress: false,
  setExpensesExportInProgress: (value) =>
    set({ isExpensesExportInProgress: value }),
  activeChiftExportIdBanks: null,
  setActiveChiftExportIdBanks: (id) => set({ activeChiftExportIdBanks: id }),
  activeChiftExportIdRevenues: null,
  setActiveChiftExportIdRevenues: (id) =>
    set({ activeChiftExportIdRevenues: id }),
  activeChiftExportIdExpenses: null,
  setActiveChiftExportIdExpenses: (id) =>
    set({ activeChiftExportIdExpenses: id }),
  chiftExportToastContextBanks: null,
  setChiftExportToastContextBanks: (ctx) =>
    set({ chiftExportToastContextBanks: ctx }),
  chiftExportToastContextRevenues: null,
  setChiftExportToastContextRevenues: (ctx) =>
    set({ chiftExportToastContextRevenues: ctx }),
  chiftExportToastContextExpenses: null,
  setChiftExportToastContextExpenses: (ctx) =>
    set({ chiftExportToastContextExpenses: ctx }),
}));

export const useChiftExportLoadingStore = store;

/** Select state for one universe (transaction = banks). Use for loading UI and provider polling. */
export function getChiftExportUniverseSelector(universe: ChiftExportUniverse) {
  const key = CHIFT_UNIVERSE_STORE_KEYS[universe];
  const prefix =
    key === 'Banks'
      ? 'isBanks'
      : key === 'Revenues'
      ? 'isRevenues'
      : 'isExpenses';
  const inProgressKey =
    `${prefix}ExportInProgress` as keyof ChiftExportLoadingStore;
  const activeIdKey =
    `activeChiftExportId${key}` as keyof ChiftExportLoadingStore;
  const ctxKey =
    `chiftExportToastContext${key}` as keyof ChiftExportLoadingStore;
  return (state: ChiftExportLoadingStore): ChiftExportUniverseState => ({
    isInProgress: state[inProgressKey] as boolean,
    activeExportId: state[activeIdKey] as string | null,
    toastContext: state[ctxKey] as ChiftExportToastContext | null,
  });
}

/** Get setters for one universe. Use with store.getState() or inside a component. */
export function getChiftExportUniverseSetters(universe: ChiftExportUniverse) {
  const key = CHIFT_UNIVERSE_STORE_KEYS[universe];
  const set = store.getState();
  const prefix =
    key === 'Banks'
      ? 'setBanks'
      : key === 'Revenues'
      ? 'setRevenues'
      : 'setExpenses';
  return {
    setInProgress: set[
      `${prefix}ExportInProgress` as keyof ChiftExportLoadingStore
    ] as (v: boolean) => void,
    setActiveExportId: set[
      `setActiveChiftExportId${key}` as keyof ChiftExportLoadingStore
    ] as (id: string | null) => void,
    setToastContext: set[
      `setChiftExportToastContext${key}` as keyof ChiftExportLoadingStore
    ] as (ctx: ChiftExportToastContext | null) => void,
  };
}

/** Clear one universe's export state. ChiftExportProvider calls this after it reads toastContext and shows the completion toast. */
export function clearChiftExportUniverse(universe: ChiftExportUniverse): void {
  const setters = getChiftExportUniverseSetters(universe);
  setters.setInProgress(false);
  setters.setActiveExportId(null);
  setters.setToastContext(null);
}

export default useChiftExportLoadingStore;
